import React, { useState, useRef } from 'react';
import ReCAPTCHA from "react-google-recaptcha";

const Contact = () => {
  const [formData, setFormData] = useState({
    subject: '',
    name: '',
    email: '',
    message: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  
  const recaptchaRef = useRef();
  
  const subjects = [
    "Commercial use request",
    "Error with search page",
    "New figure or function request",
    "General inquiry"
  ];
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.subject) {
      errors.subject = 'Please select a subject';
    }
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleCaptchaChange = (value) => {
    setCaptchaVerified(!!value);
  };
  
  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) {
    return;
  }
  
  if (!captchaVerified) {
    alert('Please complete the reCAPTCHA verification');
    return;
  }
  
  setIsSubmitting(true);
  
  try {
    // Make the actual API call to the send-contact-email.js endpoint
    const response = await fetch('/api/send-contact-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...formData,
        recaptchaToken: recaptchaRef.current.getValue()
      })
    });
    
    // Process the actual response
    const data = await response.json();
    
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to send email');
    }
    
    // Show success message and reset form
    setSubmitSuccess(true);
    setFormData({
      subject: '',
      name: '',
      email: '',
      message: ''
    });
    setCaptchaVerified(false);
    recaptchaRef.current.reset();
    
  } catch (error) {
    console.error('Error sending contact form:', error);
    alert(`There was an error submitting your message: ${error.message}. Please try again.`);
  } finally {
    setIsSubmitting(false);
  }
};
  
  return (
    <div className="contact-page">
      <h1>Contact Us</h1>
      
      <div className="contact-container">
        <div className="contact-info">
          <h2>Get in Touch</h2>
          <p>We're here to help with any questions about the DFCI MM Data Access Portal. Please fill out the form, and our team will get back to you as soon as possible.</p>
          
          <div className="contact-details">
            <div className="contact-details">
    			<p>This portal is under development by scientists at DFCI Multiple Myeloma Research group. For any questions please use the form on the right.</p>
  			</div>
          </div>
        </div>
        
        <div className="contact-form-container">
          {submitSuccess ? (
            <div className="success-message">
              <h3>Thank You!</h3>
              <p>Your message has been sent successfully. We'll get back to you as soon as possible.</p>
              <button 
                className="send-new-message" 
                onClick={() => setSubmitSuccess(false)}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className={formErrors.subject ? 'error' : ''}
                >
                  <option value="">-- Select a subject --</option>
                  {subjects.map((subject, index) => (
                    <option key={index} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
                {formErrors.subject && <span className="error-message">{formErrors.subject}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="name">Your Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={formErrors.name ? 'error' : ''}
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Your Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={formErrors.email ? 'error' : ''}
                />
                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={formErrors.message ? 'error' : ''}
                ></textarea>
                {formErrors.message && <span className="error-message">{formErrors.message}</span>}
              </div>
              
              <div className="recaptcha-container">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6Lfmi-cqAAAAAMEJNTKmi5fZ0Jc9HDb_98nEPDF9" // Replace with your actual site key
                  onChange={handleCaptchaChange}
                />
              </div>
              
              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Submit Message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;